import { ReactNode } from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { IconMenuActions } from "@/lib/Components/Actions/IconMenuActions";
import {
  supplierAcknowledgementList,
  SupplierAcknowledgementListItem,
} from "@/app/Suppliers/GraphQL/supplierAcknowledgementList";
import { useSupplierAcknowledgementActions } from "@/app/Suppliers/Hooks/useSupplierAcknowledgementActions";
import { SupplierAcknowledgementCreateDialog } from "@/app/Suppliers/Components/Dialogs/SupplierAcknowledgementCreateDialog";
import { SupplierAcknowledgementUpdateDialog } from "@/app/Suppliers/Components/Dialogs/SupplierAcknowledgementUpdateDialog";

export function SupplierAcknowledgementGrid({
  supplierId,
  addLabel = "Add acknowledgement",
}: {
  supplierId: string;
  addLabel?: string;
}) {
  const getActions = useSupplierAcknowledgementActions();
  const { open: openCreate } = useDialog(SupplierAcknowledgementCreateDialog);
  const { open: openEdit } = useDialog(SupplierAcknowledgementUpdateDialog);

  const { data } = useGqlQuery(supplierAcknowledgementList, {
    supplierId,
    page: 1,
    first: 100,
  });

  const acknowledgements = data?.supplierAcknowledgements.data ?? [];

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {acknowledgements.map((acknowledgement) => (
        <AcknowledgementCard
          key={acknowledgement.id}
          acknowledgement={acknowledgement}
          actions={<IconMenuActions actions={getActions(acknowledgement)} />}
          onEdit={() =>
            openEdit({ supplierAcknowledgementId: acknowledgement.id })
          }
        />
      ))}

      <button
        type="button"
        onClick={() => openCreate({ supplierId })}
        className="flex min-h-[120px] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-200 p-4 text-sm font-semibold text-gray-500 transition hover:border-indigo-300 hover:bg-indigo-50/40 hover:text-indigo-600 focus:outline-none focus-visible:border-indigo-400"
      >
        <PlusIcon className="size-6" />
        {addLabel}
      </button>
    </div>
  );
}

function AcknowledgementCard({
  acknowledgement,
  actions,
  onEdit,
}: {
  acknowledgement: SupplierAcknowledgementListItem;
  actions: ReactNode;
  onEdit: () => void;
}) {
  return (
    <div className="relative flex flex-col rounded-lg border border-gray-200 bg-white transition hover:border-gray-300 hover:shadow-sm">
      <div className="absolute right-2 top-2">{actions}</div>

      <button
        type="button"
        onClick={onEdit}
        className="flex flex-1 flex-col p-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40"
      >
        <span className="truncate pr-6 font-semibold text-gray-900">
          {acknowledgement.name}
        </span>
        <p className="mt-1 line-clamp-4 whitespace-pre-line text-sm text-gray-500">
          {acknowledgement.acknowledgement}
        </p>
      </button>
    </div>
  );
}
